import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import WorkflowProgressIndicator from './WorkflowProgressIndicator';
import { ROLES } from '../../constants/roles';

const getStatusColor = (status) => {
  const colors = {
    'DRAFT': 'default',
    'PENDING_OPS': 'warning',
    'CREW_ASSIGNMENT': 'info',
    'ASSIGNED': 'info',
    'CREW_ACCEPTED': 'success',
    'CREW_REJECTED': 'error',
    'READY_FOR_BA': 'warning',
    'BA_PASSED': 'success',
    'BA_FAILED': 'error',
    'READY_FOR_ATC': 'info',
    'CLEARED': 'info',
    'ATC_PASSED': 'success',
    'ATC_FAILED': 'error',
    'COMPLETED': 'success',
    'SECTOR_COMPLETED': 'success',
    'CANCELLED': 'error',
    'PLANNED': 'default'
  };
  return colors[status] || 'default';
};

const getNextAction = (currentStatus, entityType = 'sector', { sector, trip } = {}) => {
  if (entityType === 'sector' && currentStatus === 'CREW_ACCEPTED') {
    const req = sector?.requirements;
    const hasRequirements = !!(
      (req && (req.fuelKg != null || req.fuelLitres != null || req.fuel != null || req.altitude != null || req.route || req.weather)) ||
      sector?.weatherMediaId
    );
    const hasTripKit = !!(
      (sector?.tripKitMediaIds && sector.tripKitMediaIds.length) ||
      sector?.tripKitMediaId
    );
    if (hasTripKit && !sector?.tripKitVerification?.verified) {
      return {
        action: 'Verify TripKit',
        by: 'OPERATIONS_MANAGER',
        description: 'TripKit files are uploaded — Ops Manager should review and verify'
      };
    }
    if (hasRequirements) {
      const bothApproved = trip?.approvalStatus?.allApproved ||
        (trip?.approvals?.accountableManager?.approved && trip?.approvals?.camo?.approved);
      if (bothApproved) {
        return {
          action: 'Upload BA Test Video',
          by: 'CREW, Ops, AM, SUPER_ADMIN',
          description: 'Requirements are set and approvals are complete — proceed with the BA test for this sector'
        };
      }
      return {
        action: 'Await AM and CAMO Approval',
        by: 'ACCOUNTABLE_MANAGER, CA_MANAGER',
        description: 'Flight requirements are provided — awaiting charter approvals before BA test'
      };
    }
  }

  const workflowMap = {
    'DRAFT': { action: 'Send to Operations', by: 'SALES', description: 'Review and send charter to Operations team for processing' },
    'PENDING_OPS': { action: 'Assign Aircraft & Crew', by: 'OPERATIONS_MANAGER', description: 'Assign aircraft and active pilots to sectors' },
    'CREW_ASSIGNMENT': { action: 'Assign Aircraft & Crew', by: 'OPERATIONS_MANAGER', description: 'Complete aircraft and pilot assignments for all sectors' },
    'PLANNED': { action: 'Assign Aircraft & Crew', by: 'OPERATIONS_MANAGER', description: 'Assign aircraft and active pilots to this sector' },
    'ASSIGNED': { action: 'Accept/Reject Assignment', by: 'CREW', description: 'Crew members must accept or reject their assignments' },
    'CREW_ACCEPTED': { action: 'Provide Flight Requirements', by: 'OPERATIONS_MANAGER, OPERATIONS_EXECUTIVE', description: 'Provide fuel, altitude, route, weather, and TripKit (Ops Manager verifies TripKit)' },
    'CREW_REJECTED': { action: 'Reassign Crew', by: 'OPERATIONS_MANAGER', description: 'Reassign aircraft and/or crew for this sector' },
    'READY_FOR_BA': { action: 'Upload BA Test Video', by: 'CREW, Ops, AM, SUPER_ADMIN', description: 'Upload breathalyzer test video for compliance' },
    'BA_PASSED': { action: 'Request Accountable Manager Approval', by: 'OPERATIONS_MANAGER', description: 'Request Accountable Manager approval before proceeding' },
    'BA_FAILED': { action: 'Review BA Test Failure', by: 'OPERATIONS_MANAGER', description: 'Review BA test failure and take appropriate action' },
    'READY_FOR_ATC': { action: 'Request ATC Clearance', by: 'OPERATIONS_MANAGER', description: 'Request ATC clearance for the flight' },
    'CLEARED': { action: 'Sign ATC Clearance', by: 'CREW', description: 'Sign the ATC clearance document' },
    'ATC_PASSED': { action: 'Complete Flight', by: 'CREW', description: 'Complete the flight and enter the Technical Log Book' },
    'ATC_FAILED': { action: 'Review ATC Clearance Issue', by: 'OPERATIONS_MANAGER', description: 'Review ATC clearance issue and take appropriate action' },
    'COMPLETED': { action: 'Enter Techlog', by: 'CREW', description: 'Enter post-flight data in the Technical Log Book' },
    'SECTOR_COMPLETED': { action: 'Review Completion', by: 'OPERATIONS_MANAGER', description: 'Review completed sector and verify all documentation' },
    'CANCELLED': { action: 'Review Cancellation', by: 'OPERATIONS_MANAGER', description: 'Review cancellation details and handle accordingly' }
  };
  return workflowMap[currentStatus] || { action: 'Review Status', by: 'OPERATIONS_MANAGER', description: 'Review current status and determine next steps' };
};

const formatDateTime = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).format('DD MMM YYYY, HH:mm');
};

const formatActorName = (actor) => {
  if (!actor) return 'System';
  if (typeof actor === 'string') return 'Unknown User';
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
  return name || actor.email || 'Unknown User';
};

const WorkflowStatusCard = ({ trip, sector, user }) => {
  const entity = sector || trip;
  const entityType = sector ? 'sector' : 'trip';
  const currentStatus = entity?.status || 'UNKNOWN';
  
  // Get last action from statusHistory
  const statusHistory = entity?.statusHistory || [];
  const lastStatusEntry = statusHistory.length > 0 ? statusHistory[statusHistory.length - 1] : null;
  
  // Get next action
  const nextAction = getNextAction(currentStatus, entityType, { sector, trip });
  
  // Check if current user can perform next action
  const canPerformAction = () => {
    const userRole = user?.role;
    const requiredRole = nextAction.by.toUpperCase().replace(' ', '_');
    
    // Map role names
    const roleMap = {
      'SALES': [ROLES.SALES],
      'OPERATIONS_MANAGER': [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE],
      'CREW': [ROLES.CREW],
      'ACCOUNTABLE_MANAGER': [ROLES.ACCOUNTABLE_MANAGER],
      'CA_MANAGER': [ROLES.CA_MANAGER],
      'SUPER_ADMIN': [ROLES.SUPER_ADMIN]
    };
    
    const allowedRoles = roleMap[requiredRole] || [];
    return allowedRoles.includes(userRole) || userRole === ROLES.SUPER_ADMIN;
  };

  const userCanAct = canPerformAction();

  return (
    <Card sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Workflow Status</Typography>
        </Box>
        
        <Grid container spacing={2}>
          {/* Current Status */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Status
            </Typography>
            <Chip 
              label={currentStatus.replace(/_/g, ' ')} 
              color={getStatusColor(currentStatus)}
              size="small"
              sx={{ mb: 1 }}
            />
            {sector && (
              <Typography variant="caption" display="block" color="text.secondary">
                Sector {sector.seqNo}
              </Typography>
            )}
          </Grid>

          {/* Last Action */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Last Action
            </Typography>
            {lastStatusEntry ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {lastStatusEntry.status.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  <PersonIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  {formatActorName(lastStatusEntry.actorId)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatDateTime(lastStatusEntry.changedAt)}
                </Typography>
                {lastStatusEntry.remarks && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    "{lastStatusEntry.remarks}"
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No actions recorded yet
              </Typography>
            )}
          </Grid>

          {/* Next Action */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Next Action Required
            </Typography>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: userCanAct ? 'success.light' : 'info.light', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: userCanAct ? 'success.main' : 'info.main'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <ArrowForwardIcon sx={{ fontSize: 16, mr: 0.5, color: userCanAct ? 'success.main' : 'info.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {nextAction.action}
                </Typography>
              </Box>
              <Typography variant="caption" display="block" color="text.secondary">
                By: {nextAction.by}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {nextAction.description}
              </Typography>
              {userCanAct && (
                <Chip 
                  icon={<CheckCircleIcon />}
                  label="You can perform this action"
                  color="success"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
        
        {/* Workflow Progress Indicator */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">View Workflow Progress</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <WorkflowProgressIndicator 
              currentStatus={currentStatus} 
              entityType={entityType}
            />
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default WorkflowStatusCard;

